import { AlertTriangle, Check, Circle, Loader2 } from "lucide-react";
import React from "react";

import type { OzonChange } from "@/api/ozonSupply";
import { cn } from "@/lib/utils";
import { stepsOf, type StepStatus } from "./changeModel";

/** Где стоит каскад: шаг · состояние · чем сорвался.
 *
 *  ⚠️ Список ПОЛНЫЙ и всегда один и тот же: человек, у которого правка встала на «распроведении
 *  1С», должен видеть, что перед этим уже удалены коды и досланы места, — то есть что назад
 *  дороги нет. Одна строка «идёт применение» это скрывала бы.
 */
export function ChangeApplyProgress({
  change,
  palletsToPortal,
}: {
  change: OzonChange;
  /** паллеты — в портал склада: в каскаде есть шаг плана паллет */
  palletsToPortal: boolean;
}): React.ReactElement {
  const steps = stepsOf(change, palletsToPortal);
  return (
    <div className="space-y-1.5 rounded-md border bg-card px-3 py-2 text-xs">
      <div className="font-medium">
        Применение изменения №{change.id}
        {change.state === "done" && " — готово"}
      </div>
      <ol className="space-y-1">
        {steps.map((s, i) => (
          <li key={s.key} className="flex items-start gap-2">
            <StepIcon status={s.status} />
            <div className="min-w-0 flex-1">
              <span
                className={cn(
                  s.status === "waiting" && "text-muted-foreground",
                  s.status === "failed" && "text-red-700 dark:text-red-300",
                  s.status === "running" && "font-medium",
                )}
              >
                {i + 1}. {s.label}
              </span>
              {s.status === "running" && (
                <span className="text-muted-foreground"> — идёт…</span>
              )}
              {s.error && (
                <div className="mt-0.5 rounded border border-red-300 bg-red-50 px-2 py-1 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
                  {s.error}
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
      {/* Протокол каскада — `notes` СОХРАНЁННОГО плана: по нему и шли шаги, а не по сегодняшней
          раскладке. Пока плана нет (заход ещё в правке), показывать нечего */}
      {(change.plan?.notes ?? []).length > 0 && (
        <div className="space-y-0.5 border-t pt-1.5 text-muted-foreground">
          {change.plan!.notes.map((n) => (
            <div key={n}>· {n}</div>
          ))}
        </div>
      )}
      {change.state === "done" &&
        change.labels_to_reprint.positions.length > 0 && (
          <div className="border-t pt-1.5">
            {/* ⚠️ Клеить идут на НОВЫЕ коробы: у изменённого места уцелевшие коробы стоят со
                своими наклейками, и «этикетки места №1» звало бы переклеить всю паллету */}
            Перепечатать этикетки новых коробов мест №
            {change.labels_to_reprint.positions.join(", ")} (
            {change.labels_to_reprint.codes} кодов)
            {change.labels_to_reprint.printed_at === null
              ? " — стопка ещё не печаталась."
              : " — напечатаны."}
          </div>
        )}
    </div>
  );
}

function StepIcon({ status }: { status: StepStatus }): React.ReactElement {
  if (status === "done")
    return (
      <Check className="mt-0.5 size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
    );
  if (status === "running")
    return <Loader2 className="mt-0.5 size-3.5 shrink-0 animate-spin" />;
  if (status === "failed")
    return (
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-red-600 dark:text-red-400" />
    );
  return (
    <Circle className="mt-0.5 size-3.5 shrink-0 text-muted-foreground/40" />
  );
}
